import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/Layout/AdminLayout';
import { Users, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
    const { admin, loading: authLoading } = useAuth();
    const [stats, setStats] = useState({
        totalStudents: 0,
        completedAssessments: 0,
        passedAssessments: 0,
        pendingInvites: 0,
        passRate: 0
    });
    const [recentResults, setRecentResults] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Only fetch dashboard data when user is authenticated
        if (!authLoading && admin) {
            fetchDashboardData();
        } else if (!authLoading && !admin) {
            setLoading(false);
        }
    }, [authLoading, admin]);

    const fetchDashboardData = async () => {
        try {
            const response = await axios.get('/api/admin/dashboard');
            setStats(response.data.stats);
            setRecentResults(response.data.recentResults);
        } catch (error) {
            toast.error('Failed to load dashboard data');
            console.error('Dashboard fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        {
            title: 'Total Students',
            value: stats.totalStudents,
            icon: Users,
            color: 'var(--primary-accent)',
            bgColor: 'rgba(0, 89, 255, 0.1)'
        },
        {
            title: 'Completed Assessments',
            value: stats.completedAssessments,
            icon: CheckCircle,
            color: 'var(--success)',
            bgColor: 'rgba(40, 167, 69, 0.1)'
        },
        {
            title: 'Pass Rate',
            value: `${stats.passRate}%`,
            icon: TrendingUp,
            color: 'var(--info)',
            bgColor: 'rgba(23, 162, 184, 0.1)'
        },
        {
            title: 'Pending Invites',
            value: stats.pendingInvites,
            icon: Clock,
            color: 'var(--warning)',
            bgColor: 'rgba(255, 193, 7, 0.1)'
        }
    ];

    if (loading) {
        return (
            <AdminLayout title="Dashboard">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="loading-spinner"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Dashboard">
            <div className="dashboard">
                {/* Stats Cards */}
                <div className="stats-grid">
                    {statCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={stat.title}
                                className="stat-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                style={{ backgroundColor: stat.bgColor }}
                            >
                                <div className="stat-icon" style={{ color: stat.color }}>
                                    <Icon size={24} />
                                </div>
                                <div className="stat-content">
                                    <h3 className="stat-value">{stat.value}</h3>
                                    <p className="stat-title">{stat.title}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Recent Results */}
                <motion.div
                    className="card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <div className="card-header">
                        <h2 className="card-title">Recent Assessment Results</h2>
                        <p className="card-subtitle">Latest completed assessments</p>
                    </div>

                    {recentResults.length > 0 ? (
                        <div className="results-list">
                            {recentResults.map((result, index) => (
                                <motion.div
                                    key={result._id}
                                    className="result-item"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 + index * 0.1 }}
                                >
                                    <div className="result-student">
                                        <h4>{result.student.name}</h4>
                                        <p>{result.student.email}</p>
                                    </div>
                                    <div className="result-score">
                                        <span className={`score-badge ${result.passed ? 'passed' : 'failed'}`}>
                                            {result.percentage}%
                                        </span>
                                    </div>
                                    <div className="result-status">
                                        {result.passed ? (
                                            <CheckCircle size={20} className="text-success" />
                                        ) : (
                                            <XCircle size={20} className="text-danger" />
                                        )}
                                        <span className={result.passed ? 'text-success' : 'text-danger'}>
                                            {result.passed ? 'Passed' : 'Failed'}
                                        </span>
                                    </div>
                                    <div className="result-date">
                                        {new Date(result.completedAt).toLocaleDateString()}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <CheckCircle size={48} className="text-muted" />
                            <h3>No results yet</h3>
                            <p>Assessment results will appear here once students start completing assessments.</p>
                        </div>
                    )}
                </motion.div>

                {/* Quick Actions */}
                <motion.div
                    className="quick-actions"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <h3>Quick Actions</h3>
                    <div className="actions-grid">
                        <a href="/admin/students" className="action-card">
                            <Users size={24} />
                            <span>Invite Students</span>
                        </a>
                        <a href="/admin/assessments" className="action-card">
                            <CheckCircle size={24} />
                            <span>Manage Assessment</span>
                        </a>
                        <a href="/admin/results" className="action-card">
                            <TrendingUp size={24} />
                            <span>View All Results</span>
                        </a>
                    </div>
                </motion.div>
            </div>

            <style jsx>{`
        .dashboard {
          max-width: 1200px;
          margin: 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          padding: 24px;
          border-radius: var(--border-radius-lg);
          display: flex;
          align-items: center;
          gap: 16px;
          border: 1px solid rgba(0, 0, 0, 0.05);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--border-radius);
          background-color: rgba(255, 255, 255, 0.8);
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: var(--text-primary);
        }

        .stat-title {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 4px 0 0 0;
        }

        .results-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
          background-color: var(--gray-200);
          border-radius: var(--border-radius);
          overflow: hidden;
        }

        .result-item {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 16px;
          padding: 16px 20px;
          background-color: var(--white);
          align-items: center;
        }

        .result-student h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .result-student p {
          margin: 4px 0 0 0;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .score-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
        }

        .score-badge.passed {
          background-color: rgba(40, 167, 69, 0.1);
          color: var(--success);
        }

        .score-badge.failed {
          background-color: rgba(220, 53, 69, 0.1);
          color: var(--danger);
        }

        .result-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .result-date {
          font-size: 14px;
          color: var(--text-secondary);
          text-align: right;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: var(--text-primary);
        }

        .empty-state p {
          color: var(--text-secondary);
          margin: 0;
        }

        .quick-actions {
          margin-top: 32px;
        }

        .quick-actions h3 {
          margin-bottom: 16px;
          color: var(--text-primary);
        }

        .actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .action-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px;
          background-color: var(--white);
          border: 2px solid var(--gray-200);
          border-radius: var(--border-radius-lg);
          text-decoration: none;
          color: var(--text-primary);
          transition: var(--transition);
        }

        .action-card:hover {
          border-color: var(--primary-accent);
          color: var(--primary-accent);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .result-item {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .result-date {
            text-align: left;
          }

          .actions-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
        </AdminLayout>
    );
};

export default AdminDashboard;
